import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { FolderOpen, Clock, ArrowRight, Layers, Search, X, SlidersHorizontal, MessageCircle, User as UserIcon, Briefcase, Globe } from "lucide-react";
import { motion } from "framer-motion";
import GuestCTA from "@/components/GuestCTA";
import ProjectPreview2D from "@/components/ProjectPreview2D";
import { formatHoursRangeWithDays } from "@/lib/hours";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const complexityColor = {
  "Niedrig": "#00F0FF", "Mittel": "#FFB800", "Hoch": "#FF3B30", "Sehr Hoch": "#FF3B30",
};

const CATEGORIES = ["", "Web Development", "Mobile App", "3D/CAD Design"];
const COMPLEXITIES = ["", "Niedrig", "Mittel", "Hoch", "Sehr Hoch"];

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const role = user?.role || "client";
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [complexity, setComplexity] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  // For clients: "mine" = own projects, "marketplace" = all public projects.
  // For freelancers: always "marketplace".
  // For guests (not logged in): always "marketplace".
  const isLoggedIn = !!user;
  const [viewMode, setViewMode] = useState(
    !isLoggedIn || role === "freelancer" ? "marketplace" : "mine"
  );
  // Reset view mode whenever the auth state flips (login/logout/role change)
  // so a client landing freshly always sees "Meine Projekte", a freelancer
  // sees the marketplace, and a guest never sees private listings.
  useEffect(() => {
    if (!isLoggedIn || role === "freelancer") {
      setViewMode("marketplace");
    } else if (role === "client") {
      setViewMode("mine");
    }
  }, [role, isLoggedIn]);

  const fetchProjects = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (category) params.set("category", category);
    if (complexity) params.set("complexity", complexity);
    if (viewMode === "mine") params.set("mine", "true");
    axios.get(`${API}/projects?${params.toString()}`, { withCredentials: true })
      .then(res => setProjects(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, category, complexity, viewMode]);

  useEffect(() => {
    const t = setTimeout(fetchProjects, 300);
    return () => clearTimeout(t);
  }, [fetchProjects]);

  const activeFilters = [category, complexity].filter(Boolean).length;
  const isMarketplace = viewMode === "marketplace";

  return (
    <div data-testid="dashboard-page" className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light tracking-tight text-white">
              {!isLoggedIn || role === "freelancer" || isMarketplace
                ? (role === "freelancer" ? t.dashboard.title_freelancer : "Marktplatz")
                : t.dashboard.title_client}
            </h1>
            <p className="text-[#A0A0B0] mt-1 text-sm">
              {!isLoggedIn || role === "freelancer" || isMarketplace
                ? (role === "freelancer" ? t.dashboard.sub_freelancer : "Stöbere durch alle öffentlich gelisteten Projekte auf der Plattform.")
                : t.dashboard.sub_client}
            </p>
          </div>
          {role === "client" && user && (
            <Link to="/new-project" data-testid="dashboard-new-project-btn">
              <button className="btn-primary flex items-center gap-2 text-sm">{t.dashboard.new} <ArrowRight className="w-4 h-4" /></button>
            </Link>
          )}
        </div>

        {/* Guest CTA banner */}
        {!user && (
          <div className="mb-6">
            <GuestCTA
              testId="dashboard-guest-cta"
              message="Du erkundest Project Radar AR im Vorschau-Modus. Du kannst alle öffentlichen Projekte sehen — für Chat, Bewerbungen oder eigene Projekte erstelle bitte ein kostenloses Konto."
              ctaLabel="Konto erstellen"
            />
          </div>
        )}

        {/* View Toggle (only for logged-in clients - freelancers + guests always see marketplace) */}
        {isLoggedIn && role === "client" && (
          <div className="flex mb-6 glass-surface rounded-lg p-1 max-w-md" data-testid="dashboard-view-toggle">
            <button
              data-testid="view-mine-btn"
              onClick={() => setViewMode("mine")}
              className={`flex-1 py-2 px-4 text-sm font-['Outfit'] font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
                viewMode === "mine"
                  ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20"
                  : "text-[#A0A0B0] hover:text-white"
              }`}
            >
              <Briefcase className="w-3.5 h-3.5" /> Meine Projekte
            </button>
            <button
              data-testid="view-marketplace-btn"
              onClick={() => setViewMode("marketplace")}
              className={`flex-1 py-2 px-4 text-sm font-['Outfit'] font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
                viewMode === "marketplace"
                  ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20"
                  : "text-[#A0A0B0] hover:text-white"
              }`}
            >
              <Globe className="w-3.5 h-3.5" /> Marktplatz
            </button>
          </div>
        )}

        {/* Search & Filters */}
        <div className="mb-8 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-[#606070]" />
              <input
                data-testid="search-input"
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t.dashboard.search}
                className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] pl-10 pr-10 py-2.5 placeholder:text-white/30 text-sm transition-colors outline-none rounded-lg"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-3">
                  <X className="w-4 h-4 text-[#606070] hover:text-white" />
                </button>
              )}
            </div>
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters(p => !p)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-['Outfit'] transition-all ${
                showFilters || activeFilters > 0
                  ? "border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF]"
                  : "border-white/10 bg-white/5 text-[#A0A0B0] hover:bg-white/10"
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {t.dashboard.filter}
              {activeFilters > 0 && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#00F0FF] text-black text-[10px] font-bold">{activeFilters}</span>
              )}
            </button>
          </div>

          {showFilters && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="flex gap-3 flex-wrap">
              <select
                data-testid="filter-category"
                value={category} onChange={e => setCategory(e.target.value)}
                className="bg-black/50 border border-white/10 text-white px-3 py-2 text-sm rounded-lg font-['JetBrains_Mono'] outline-none appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#0F0F16]">{t.dashboard.all_categories}</option>
                {CATEGORIES.filter(Boolean).map(c => <option key={c} value={c} className="bg-[#0F0F16]">{c}</option>)}
              </select>
              <select
                data-testid="filter-complexity"
                value={complexity} onChange={e => setComplexity(e.target.value)}
                className="bg-black/50 border border-white/10 text-white px-3 py-2 text-sm rounded-lg font-['JetBrains_Mono'] outline-none appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#0F0F16]">{t.dashboard.all_complexities}</option>
                {COMPLEXITIES.filter(Boolean).map(c => <option key={c} value={c} className="bg-[#0F0F16]">{c}</option>)}
              </select>
              {activeFilters > 0 && (
                <button onClick={() => { setCategory(""); setComplexity(""); }} className="text-xs text-[#FF3B30] hover:underline flex items-center gap-1">
                  <X className="w-3 h-3" /> {t.dashboard.reset_filters}
                </button>
              )}
            </motion.div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!loading && projects.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-surface rounded-lg p-16 text-center">
            <FolderOpen className="w-16 h-16 text-[#606070] mx-auto mb-4" />
            <h3 className="font-['Outfit'] text-xl text-white mb-2">
              {search || category || complexity ? t.dashboard.no_results : t.dashboard.empty}
            </h3>
            <p className="text-[#A0A0B0] mb-6">
              {search || category || complexity
                ? t.dashboard.empty_filter_hint
                : isMarketplace
                  ? "Aktuell sind keine öffentlichen Projekte gelistet. Schau später wieder vorbei."
                  : role === "client" ? t.dashboard.empty_client_hint : t.dashboard.empty_freelancer_hint}
            </p>
          </motion.div>
        )}

        {/* Grid */}
        {!loading && projects.length > 0 && (
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {projects.map((p) => {
              const isOwner = p.user_id === user?.user_id;
              const showClient = (role === "freelancer" || (role === "client" && isMarketplace)) && p.client && !isOwner;
              return (
                <motion.div key={p.id} variants={fadeUp} className="relative">
                  <Link to={`/project/${p.id}`} data-testid={`project-card-${p.id}`}>
                    <div className="glass-surface rounded-lg p-6 hover:border-[#00F0FF]/20 transition-all duration-300 group cursor-pointer h-full">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-['JetBrains_Mono'] text-[#A0A0B0] tracking-wider uppercase">{p.projectCategory}</span>
                        {p.offerSent && <span className="text-xs px-2 py-1 bg-[#00F0FF]/10 text-[#00F0FF] rounded font-['JetBrains_Mono']">{t.dashboard.offer}</span>}
                      </div>
                      <h3 className="font-['Outfit'] text-lg font-medium text-white mb-3 group-hover:text-[#00F0FF] transition-colors">{p.projectTitle}</h3>

                      {/* Compact 2D UI thumbnail (zero-cost CSS wireframe) */}
                      {p.analysis?.visualElements?.length > 0 && (
                        <ProjectPreview2D
                          project={p}
                          variant="landing"
                          compact={true}
                          showDisclaimer={false}
                          className="mb-3"
                        />
                      )}

                      {showClient && (
                        <div data-testid={`project-client-${p.id}`} className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
                          {p.client.picture ? (
                            <img src={p.client.picture} alt={p.client.name} className="w-6 h-6 rounded-full border border-white/10" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-center justify-center">
                              <UserIcon className="w-3 h-3 text-[#00F0FF]" />
                            </div>
                          )}
                          <span className="text-xs text-[#A0A0B0]">
                            <span className="text-[#606070]">von</span> <span className="text-white font-medium">{p.client.name}</span>
                          </span>
                        </div>
                      )}

                      {p.analysis && (
                        <div className="space-y-3 mt-4 pt-4 border-t border-white/5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-[#606070]">{t.dashboard.complexity}</span>
                            <span className="text-xs font-['JetBrains_Mono'] font-medium" style={{ color: complexityColor[p.analysis.projectComplexity] || "#A0A0B0" }}>
                              {p.analysis.projectComplexity}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-[#606070]">{t.dashboard.effort}</span>
                            <span className="text-xs font-['JetBrains_Mono'] text-white">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatHoursRangeWithDays(p.analysis.estimatedHours_min, p.analysis.estimatedHours_max)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 flex-wrap mt-2">
                            {p.analysis.techStack_reco.slice(0, 3).map((tech, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 text-[#A0A0B0] rounded font-['JetBrains_Mono']">{tech}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5 text-[#00F0FF] opacity-0 group-hover:opacity-100 transition-opacity">
                        {showClient ? (
                          <>
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-xs font-['Outfit']">3D-Vorschau & Chat öffnen</span>
                          </>
                        ) : (
                          <>
                            <Layers className="w-4 h-4" />
                            <span className="text-xs font-['Outfit']">{t.dashboard.preview_3d}</span>
                          </>
                        )}
                        <ArrowRight className="w-3 h-3 ml-auto" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}
